/**
 * pages/JobEditor.tsx — creating and editing a scheduled update.
 *
 * The form is deliberately short, and the two longest passages of prose on it are there to prevent two
 * specific misunderstandings that cost real work when they happen:
 *
 * **1. Choosing a Confluence page hands that page over.** Every publish regenerates the page body from
 * Ticketless's own records ([ADR 0004](../../../docs/adr/0004-confluence-page-as-a-projection.md) — the
 * page is a projection of the database, which is precisely what makes it safe to write to). Anything a
 * person types on it is gone at the next run. That warning sits beside the space and title fields, at the
 * moment of choosing, because a warning in help text is a warning nobody read until afterwards.
 *
 * **2. Wording and format are not settings.** Users control inputs and destinations only; there is no
 * prompt, tone, template or icon field anywhere in the schema and none may be added. Rather than leave a
 * user hunting for the setting, the form shows the icon vocabulary and a live example of the house format
 * built from what they have typed — the fastest way to establish that this is the output, not a default.
 *
 * **3. One of the three kinds can COMMIT A FILE to the chosen repository.** An executive summary or a
 * security overview relays the repository's own `EXECUTIVE.md` / `SECURITY.md`; where the file is absent
 * the update writes a first draft and pushes it before it publishes anything. That is the only thing this
 * application does outside its own database, and it happens on a schedule, unattended, to a repository
 * somebody else owns. So it is a warning of the same weight as the page-ownership one above, sitting
 * beside the repository picker — the control that causes it — rather than in help text under it. The
 * counterpart promise is stated in the same breath, because it is what makes the first half acceptable:
 * Ticketless can only ever CREATE that file. `GitWriter` has no update method, so a document that exists
 * is relayed and never touched.
 *
 * **The repository list is BU-scoped on the server and is never filtered here.** `GET /api/repos` already
 * applies `registry ∩ floors ∩ decision` (`server/catalogue/index.ts`); a second opinion in the client
 * would be a second decision path, and the more permissive of two decision paths becomes the policy.
 *
 * **A save is several calls and is not atomic**, because the API has no replace-the-whole-job route: the
 * job row, each repository and each destination are separately permission-checked writes. So the save
 * runs the job write first and then reconciles repositories and destinations against what was loaded, and
 * a refusal partway through leaves the earlier writes standing. That is visible rather than hidden — the
 * form reloads from the server on failure, so what is on screen is what was actually stored.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { CATEGORIES, CATEGORY_ICONS, CATEGORY_LABELS } from "../../../server/changelog/types";
import { ApiError, api, type AdminUser, type Cadence, type Job, type UpdateKind } from "../lib/api";
import { JobAdmin, jobAdminPorts, type JobAdminPorts } from "./JobAdmin";
import { formatMinuteOfDay, updateKindHint, updateKindLabel } from "../lib/format";
import type { JobRepoView, JobTargetView } from "./Dashboard";
import {
  FormActions,
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  ErrorState,
  LoadingState,
  MultiSelect,
  Page,
  RadioGroup,
  Select,
  TextField,
} from "../components/ui";

/* ---------------------------------------------------------------------------------------------------
 * Wire shapes, transcribed from `server/routes/catalogue.ts` and `server/routes/jobs.ts`
 * ------------------------------------------------------------------------------------------------- */

/** One offerable repository. `path` is the stable identifier a job stores; the rest is orientation. */
export interface CatalogueRepo {
  readonly path: string;
  readonly cloneUrl: string;
  readonly branch: string;
  readonly workspacePath: string;
}

interface CatalogueResponse {
  readonly buId: string;
  readonly repositories: readonly CatalogueRepo[];
}

export interface JobDetail {
  readonly job: Job & {
    readonly ignorePaths?: readonly string[];
    readonly ignoreAuthors?: readonly string[];
  };
  readonly repos: readonly JobRepoView[];
  readonly targets: readonly JobTargetView[];
}

export interface JobEditorData {
  readonly repositories: readonly CatalogueRepo[];
  /** Null when creating. */
  readonly job: JobDetail | null;
}

/* ---------------------------------------------------------------------------------------------------
 * The draft
 * ------------------------------------------------------------------------------------------------- */

export interface ConfluenceDestination {
  readonly spaceKey: string;
  readonly pageTitle: string;
  /**
   * Present ⇒ the destination was resolved from a pasted link and is bound to the PAGE rather than to its
   * title. A page renamed in Confluence keeps its id, and this is what stops the next publish creating a
   * second page under the old name.
   */
  readonly pageId?: string;
}

/** What `POST /api/confluence/resolve` answers with. Every field of it came from the Confluence API. */
export interface ResolvedPage {
  readonly spaceKey: string;
  readonly pageId: string;
  readonly pageTitle: string;
  readonly url: string;
  /** `decoded` ⇒ the id came from a short link and was computed. See the confirmation block's warning. */
  readonly idConfidence: "explicit" | "decoded";
}

/** How the destination is being chosen. Both are first-class; neither is the edge case. */
type PageMode = "paste" | "name";

/**
 * Identity of a destination within the draft.
 *
 * By page id where there is one, so that the same page added once by link and once by name collides in
 * the form rather than at the server — a 409 after pressing Save is a worse way to learn it.
 */
function destinationKey(destination: ConfluenceDestination): string {
  return destination.pageId ?? `${destination.spaceKey}:${destination.pageTitle}`;
}

export interface JobDraft {
  readonly name: string;
  /**
   * What this update says. Sent on CREATE and never on a save of an existing update — see `saveViaApi`,
   * which is where that distinction is actually kept, and `lib/api.ts`'s `Job.kind` for why the server
   * refuses to change it.
   *
   * It sits on the draft rather than beside it because the draft is what `validateDraft` reads, and three
   * of this form's rules now depend on the kind: which repository picker is on screen, how many
   * repositories are enough, and whether the two changelog-only cards exist to be validated at all.
   */
  readonly kind: UpdateKind;
  readonly cadence: Cadence;
  readonly timezone: string;
  readonly sendAtMinute: number;
  readonly sendOnWeekday: number | null;
  /** Hours between runs, for `hourly`. At least 1; there is no maximum. */
  readonly intervalHours: number;
  readonly enabled: boolean;
  readonly repoPaths: readonly string[];
  readonly confluence: readonly ConfluenceDestination[];
  readonly slackChannels: readonly string[];
  readonly backfillDays: number;
  readonly ignorePaths: readonly string[];
  readonly ignoreAuthors: readonly string[];
  readonly postWhenEmpty: boolean;
}

export interface JobEditorPorts {
  readonly load: (signal: AbortSignal) => Promise<JobEditorData>;
  /** Returns the job id. `existing` is what was loaded, and is what the reconciliation diffs against. */
  readonly save: (draft: JobDraft, existing: JobDetail | null) => Promise<string>;
  /**
   * Ask the SERVER what a pasted link points at. Throws an `ApiError` carrying the refusal's own sentence.
   *
   * A port rather than a `fetch` inside the component, like `load` and `save`, and for the same reason:
   * the interesting behaviour here is what the form does with a refusal, and that should be assertable
   * without a network.
   */
  readonly resolvePage: (url: string) => Promise<ResolvedPage>;
}

/**
 * The three kinds, in the order the form offers them: the one that reads commits, then the two that read
 * a document.
 *
 * The list is the schema's (`server/db/jobs.ts`'s `JOB_KINDS`); the WORDS are `lib/format.ts`'s, and are
 * deliberately not restated here. A kind named one thing on this form, another on the dashboard column
 * and a third in a run's heading is the drift a closed label map exists to prevent, and it starts exactly
 * by someone typing a nicer label at the one call site they happened to be editing.
 */
const UPDATE_KIND_OPTIONS: readonly UpdateKind[] = ["changelog", "executive_summary", "security_overview"];

/** The two kinds that read a document out of the repository. Never a changelog; never both. */
type DocumentKind = Exclude<UpdateKind, "changelog">;

/**
 * Document-ness by EXCLUSION, exactly as `server/db/jobs.ts`'s `isDocumentKind` derives it.
 *
 * A second literal list would be a second place a fourth kind has to be remembered, and the failure mode
 * of forgetting is silent: the new kind would be treated as a changelog by every branch here — offered a
 * multi-repository picker, validated as a changelog, and never warned about a commit it might make.
 */
function isDocumentKind(kind: UpdateKind): kind is DocumentKind {
  return kind !== "changelog";
}

/**
 * Which file each document kind is about, mirroring `engine/kinds/document.ts`'s `SOURCE_PATH`.
 *
 * Named here so the warning can say the actual filename. It is NOT configurable and must not become so:
 * the moment the source path is a field, a job can be pointed at any file in the repository and the two
 * kinds stop being two kinds. Users control inputs and destinations; this is neither.
 */
const SOURCE_FILE: Readonly<Record<DocumentKind, string>> = {
  executive_summary: "EXECUTIVE.md",
  security_overview: "SECURITY.md",
};

/**
 * `hourly` leads, and is labelled by what it does rather than by its column value.
 *
 * "Every N hours" is the frequency most updates actually want, and it is the only one here that is not a
 * position in the week — so it takes the number field beside it and the rest take a day and a time. The
 * five values are the schema's; only the wording is this file's.
 */
const CADENCE_OPTIONS: readonly { readonly value: Cadence; readonly label: string }[] = [
  { value: "hourly", label: "Every so many hours" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
];

/** The one cadence that is an interval rather than a place in the week. */
const INTERVAL_CADENCE: Cadence = "hourly";

/**
 * The floor is the product's: a job asked to publish more often than a run takes spends its life
 * colliding with its own lease. There is deliberately NO ceiling here — the server's is an arithmetic
 * limit some 228,000 years out, and a form that refused 8760 would be inventing a rule nobody wrote.
 */
export const MIN_INTERVAL_HOURS = 1;

/**
 * How far back an update reconstructs the first time it sees a repository, and the most it may be asked
 * to. These mirror `server/db/jobs.ts` and the route's own schema; a test asserts the three agree, since
 * a form that offered a hundred and twenty days would only be showing the user a 400 later.
 */
export const DEFAULT_BACKFILL_DAYS = 90;
export const MAX_BACKFILL_DAYS = 90;

/** Only these two cadences have a day to land on; the others would be answering an unasked question. */
const WEEKDAY_CADENCES: ReadonlySet<Cadence> = new Set<Cadence>(["weekly", "fortnightly"]);

const WEEKDAY_OPTIONS = [
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
  { value: "7", label: "Sunday" },
] as const;

/* ---------------------------------------------------------------------------------------------------
 * Parsing and validation — everything the server would refuse, refused here first with a sentence
 * ------------------------------------------------------------------------------------------------- */

const TIME_OF_DAY = /^([01]?\d|2[0-3]):([0-5]\d)$/;

/** "09:30" → 570. Null when it is not a time, which the caller turns into a field error. */
export function parseTimeOfDay(value: string): number | null {
  const match = TIME_OF_DAY.exec(value.trim());
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * The server refuses an unknown IANA zone rather than falling back, and it is right to: a typo accepted
 * publishes at the wrong hour for weeks before anybody works out why. The same check is cheap here.
 */
export function isKnownTimeZone(value: string): boolean {
  if (value.trim() === "") return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: value.trim() });
    return true;
  } catch {
    return false;
  }
}

/**
 * "6" → 6. Null when it is not a whole number of hours of at least one, which the caller turns into a
 * field error.
 *
 * There is no upper bound and that is not an oversight: the requirement is a minimum of one hour and no
 * maximum, so 8760 (a year) is as valid as 6. The only refusals are "not a number", "not whole" and
 * "less than an hour" — plus the server's arithmetic ceiling, which no human will type by accident and
 * which comes back as an ordinary field error if they do.
 */
export function parseIntervalHours(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < MIN_INTERVAL_HOURS) return null;
  return parsed;
}

/**
 * The interval read back in words, so the number in the box is not the only thing confirming the choice.
 *
 * "168" is a number nobody pictures; "every 168 hours — once a week" is a decision somebody can check.
 * The plain readings stop at a week because beyond that the arithmetic stops being obvious and a wrong
 * paraphrase is worse than none: "every 730 hours" is a month only in the sense that some months are 730
 * hours long.
 */
export function describeInterval(value: string): string {
  const hours = parseIntervalHours(value);
  if (hours === null) return "At least one hour. There is no upper limit.";
  if (hours === 1) return "Every hour, on the minute set below.";
  if (hours === 24) return "Every 24 hours — once a day, measured in hours rather than by the clock.";
  if (hours === 168) return "Every 168 hours — once a week, measured in hours rather than by the clock.";
  if (hours < 24 && 24 % hours === 0) {
    return `Every ${hours} hours — ${24 / hours} times a day.`;
  }
  return `Every ${hours} hours.`;
}

/**
 * "30" → 30. Null when it is not a whole number of minutes within the hour.
 *
 * The bound is 0–59 rather than 0–1439 on purpose. The stored column is minutes past MIDNIGHT and the
 * interval cadence reads it modulo 60, so accepting 570 here would store a number that silently becomes
 * 30 — the same discarded-hour trap this field exists to close, one layer down.
 */
export function parseMinutePastHour(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > 59) return null;
  return parsed;
}

/** The minute read back as the schedule will actually fire, so ":00" is visibly "on the hour". */
export function describeSendMinute(value: string): string {
  const minute = parseMinutePastHour(value);
  if (minute === null) return "A whole number from 0 to 59.";
  if (minute === 0) return "On the hour, in the zone below.";
  return `At :${String(minute).padStart(2, "0")} past the hour, in the zone below.`;
}

/**
 * The reconstruction window as a number the server will accept, whatever is in the box.
 *
 * The DEFAULT is the fallback rather than zero, because zero is a meaningful answer here ("start from
 * today") and quietly storing it would be this form deciding something on the user's behalf. Anything the
 * schema would refuse becomes the same 90 a new update starts with.
 */
export function sanitisedBackfillDays(value: string): number {
  const parsed = Number(value.trim());
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_BACKFILL_DAYS) return DEFAULT_BACKFILL_DAYS;
  return parsed;
}

/** A comma- or newline-separated list, as typed. Empty entries are dropped rather than stored blank. */
export function parseList(value: string): readonly string[] {
  return value
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== "");
}

export interface DraftErrors {
  readonly name?: string;
  readonly timezone?: string;
  /** The wall-clock send time. Every cadence but the interval one. */
  readonly sendTime?: string;
  /** Its opposite number: minutes past the hour, for the interval cadence only. */
  readonly sendMinute?: string;
  readonly intervalHours?: string;
  readonly backfillDays?: string;
  readonly repoPaths?: string;
  readonly destinations?: string;
}

/**
 * What the form will not send.
 *
 * The repository rule is the one worth stating: an update with no repositories has nothing to read, so it
 * would poll nothing, generate nothing and publish an empty page on a schedule — a job that looks healthy
 * in every counter and reports nothing for ever.
 *
 * **Every rule below is checked only on the field that is actually SHOWN**, and the kind now decides that
 * as much as the cadence does. The two changelog-only cards are absent on a document kind, so their
 * values are not refused there; a document kind's picker offers one repository, so its rule is "exactly
 * one" rather than "at least one". Refusing a stale value in a field the user cannot see is how a form
 * becomes unsavable for a reason it will not show you — the discipline the two schedule fields already
 * follow, applied to the fields the kind hides.
 */
export function validateDraft(
  draft: JobDraft,
  sendTime: string,
  backfillDays: string,
  intervalHours = String(draft.intervalHours),
  sendMinute = String(draft.sendAtMinute % 60),
): DraftErrors {
  const errors: {
    name?: string;
    timezone?: string;
    sendTime?: string;
    sendMinute?: string;
    intervalHours?: string;
    backfillDays?: string;
    repoPaths?: string;
    destinations?: string;
  } = {};

  if (draft.name.trim() === "") errors.name = "Give this update a name people will recognise in Confluence.";
  if (!isKnownTimeZone(draft.timezone)) errors.timezone = "Not an IANA time zone name — try Europe/London.";

  // Each schedule shape is checked on the field it actually shows, and never on the other one. Refusing a
  // stale value in a field the user cannot see is how a form becomes unsavable for a reason it will not
  // show you — which is why `intervalHours` was already guarded this way, and why the two time fields
  // must be now that they are alternatives rather than one field always on screen.
  if (draft.cadence === INTERVAL_CADENCE) {
    if (parseIntervalHours(intervalHours) === null) {
      errors.intervalHours = "A whole number of hours, one or more. There is no upper limit.";
    }
    if (parseMinutePastHour(sendMinute) === null) {
      errors.sendMinute = "A whole number from 0 to 59 — the minutes past each hour.";
    }
  } else if (parseTimeOfDay(sendTime) === null) {
    errors.sendTime = "Use a 24-hour time, such as 09:00.";
  }

  // A changelog input, on a card only a changelog has. A document kind never reconstructs history — it
  // reads one file as it stands today — so the stored 90 goes to the server untouched and unread, and
  // refusing it here would refuse a form that shows no such field.
  if (draft.kind === "changelog") {
    const parsedBackfill = Number(backfillDays.trim());
    if (!Number.isInteger(parsedBackfill) || parsedBackfill < 0 || parsedBackfill > MAX_BACKFILL_DAYS) {
      errors.backfillDays = `A whole number of days, 0 to ${MAX_BACKFILL_DAYS}. Zero means start from today.`;
    }
  }

  if (isDocumentKind(draft.kind)) {
    // Refused here so it can be a sentence, and refused AGAIN by the server at the route that adds a
    // repository and by the handler that runs the job. Three statements of one rule, and the two below
    // this one are not redundant: an intake rule enforced only at intake has a hole in it the width of
    // the edit route.
    if (draft.repoPaths.length !== 1) {
      errors.repoPaths = `${updateKindLabel(draft.kind)} is about one product, so it covers exactly one repository. Choose the one this update is about.`;
    }
  } else if (draft.repoPaths.length === 0) {
    errors.repoPaths = "Choose at least one repository — an update with nothing to watch reports nothing.";
  }
  if (draft.confluence.length === 0 && draft.slackChannels.length === 0) {
    errors.destinations = "Choose at least one destination, or this update will be written and never sent.";
  }

  return errors;
}

function hasErrors(errors: DraftErrors): boolean {
  return Object.values(errors).some((value) => value !== undefined);
}

/* ---------------------------------------------------------------------------------------------------
 * The default ports — the real API
 * ------------------------------------------------------------------------------------------------- */

/**
 * Identity of a destination as the user chose it, so add and remove can be diffed without ids.
 *
 * A stored target has an id and a draft one does not, which is why the diff is on the choice rather than
 * on the row: re-typing the same space and title must be recognised as the destination already there, not
 * as a removal followed by an addition that rewrites the page from scratch.
 */
export function targetKey(target: JobTargetView): string {
  if (target.kind !== "confluence") return `slack:${target.channelId ?? ""}`;
  // By ID where the row has one, and this is the subtle part: a page RENAMED in Confluence leaves the row
  // with a right `page_id` and a stale `page_title`. Keyed on the title, the next save from this form
  // would read that as a removal plus an addition — deleting the destination and re-creating it. Note
  // what makes this safe for by-title destinations too: a target acquires its `page_id` on its first
  // successful publish, so both sides of the diff see the same id and match. Changing a title is still
  // Remove-then-Add, which is correct.
  return target.pageId ? `confluence#${target.pageId}` : `confluence:${target.spaceKey ?? ""}:${target.pageTitle ?? ""}`;
}

export async function saveViaApi(draft: JobDraft, existing: JobDetail | null): Promise<string> {
  const body = {
    name: draft.name.trim(),
    /*
     * ⚠️ ON CREATE ONLY, and this spread is the whole of that rule on the client.
     *
     * One body object serves both verbs below, which is what makes the field easy to leak into the wrong
     * one: written as an ordinary property it would ride along on every PATCH this form has ever sent.
     * The server would not be fooled — `UpdateJobSchema` omits `kind` by name, and a second read of the
     * body refuses a kind that DISAGREES with the stored job — but a client that sends a field it is not
     * entitled to send is a client one route change away from changing something it must not. The kind is
     * chosen once, at the moment an update comes into existence, and the form makes that literally true
     * rather than merely enforced elsewhere.
     */
    ...(existing === null ? { kind: draft.kind } : {}),
    cadence: draft.cadence,
    timezone: draft.timezone.trim(),
    sendAtMinute: draft.sendAtMinute,
    sendOnWeekday: WEEKDAY_CADENCES.has(draft.cadence) ? draft.sendOnWeekday : null,
    // Sent on every cadence, not only the hourly one. The column is NOT NULL with a default, so a job
    // switched from "every 6 hours" to weekly and back must find its 6 where it left it rather than
    // silently at 1 — and the server ignores the field for every cadence but `hourly` anyway.
    intervalHours: draft.intervalHours,
    enabled: draft.enabled,
    backfillDays: draft.backfillDays,
    ignorePaths: [...draft.ignorePaths],
    ignoreAuthors: [...draft.ignoreAuthors],
    postWhenEmpty: draft.postWhenEmpty,
  };

  let jobId: string;
  if (existing === null) {
    const created = await api.post<{ readonly job: Job }>("/api/jobs", body);
    if (created === undefined) throw new ApiError(502, "internal_error", "no job returned", null);
    jobId = created.job.id;
  } else {
    jobId = existing.job.id;
    await api.patch(`/api/jobs/${encodeURIComponent(jobId)}`, body);
  }

  const base = `/api/jobs/${encodeURIComponent(jobId)}`;

  // Repositories. Additions first: a save that removed everything before adding anything would leave the
  // job momentarily watching nothing, and a refusal in the middle would leave it that way.
  const currentRepos = existing?.repos ?? [];
  const currentPaths = new Set(currentRepos.map((repo) => repo.repoPath));
  for (const path of draft.repoPaths) {
    if (!currentPaths.has(path)) await api.post(`${base}/repos`, { repoPath: path });
  }
  for (const repo of currentRepos) {
    if (!draft.repoPaths.includes(repo.repoPath)) {
      await api.delete(`${base}/repos`, { query: { repoPath: repo.repoPath } });
    }
  }

  // Destinations, by the same order and for the same reason.
  const currentTargets = existing?.targets ?? [];
  const currentKeys = new Set(currentTargets.map((target) => targetKey(target)));
  const wanted = new Set<string>();
  for (const destination of draft.confluence) {
    wanted.add(
      destination.pageId
        ? `confluence#${destination.pageId}`
        : `confluence:${destination.spaceKey}:${destination.pageTitle}`,
    );
  }
  for (const channelId of draft.slackChannels) wanted.add(`slack:${channelId}`);

  /*
   * ⚠️ REPLACEMENTS COME OUT FIRST — and only replacements.
   *
   * Additions-before-removals is right in general, for the reason above. It breaks for exactly one case:
   * the SAME Confluence page moving between the two forms. A linked destination keys on
   * `confluence#<id>`, a named one on `confluence:<space>:<title>`, so the two forms of one page hash
   * differently and the addition looks new. The POST then fires while the old row is still enabled and
   * still holding the page claim, and the server answers 409 — naming the user's own job as the holder,
   * which reads like a bug in the application rather than a step in their own save.
   *
   * Deleting just the row being replaced, before its replacement is added, costs nothing the general rule
   * was protecting: the job is not left watching nothing, because the replacement follows immediately.
   */
  const replaced = currentTargets.filter(
    (target) =>
      target.kind === "confluence" &&
      !wanted.has(targetKey(target)) &&
      draft.confluence.some(
        (destination) =>
          destination.spaceKey.toLowerCase() === (target.spaceKey ?? "").toLowerCase() &&
          destination.pageTitle === (target.pageTitle ?? ""),
      ),
  );
  for (const target of replaced) {
    await api.delete(`${base}/targets/${encodeURIComponent(target.id)}`);
  }
  const stillPresent = new Set(
    currentTargets.filter((target) => !replaced.includes(target)).map((target) => targetKey(target)),
  );

  for (const destination of draft.confluence) {
    const key = destination.pageId
      ? `confluence#${destination.pageId}`
      : `confluence:${destination.spaceKey}:${destination.pageTitle}`;
    if (!stillPresent.has(key)) {
      // One form or the other, never both: the server refuses a page id sent beside a space key, because
      // a client that could send both could pair an arbitrary page with an allowlisted space.
      await api.post(
        `${base}/targets`,
        destination.pageId
          ? { kind: "confluence", pageId: destination.pageId }
          : { kind: "confluence", spaceKey: destination.spaceKey, pageTitle: destination.pageTitle },
      );
    }
  }
  for (const channelId of draft.slackChannels) {
    if (!currentKeys.has(`slack:${channelId}`)) {
      await api.post(`${base}/targets`, { kind: "slack", channelId });
    }
  }
  for (const target of currentTargets) {
    // The replaced rows are already gone; deleting one twice would 404.
    if (!wanted.has(targetKey(target)) && !replaced.includes(target)) {
      await api.delete(`${base}/targets/${encodeURIComponent(target.id)}`);
    }
  }

  return jobId;
}

function defaultPorts(jobId: string | null): JobEditorPorts {
  return {
    load: async (signal) => {
      const [catalogue, job] = await Promise.all([
        api.get<CatalogueResponse>("/api/repos", { signal }),
        jobId === null
          ? Promise.resolve(null)
          : api.get<JobDetail>(`/api/jobs/${encodeURIComponent(jobId)}`, { signal }),
      ]);
      return { repositories: catalogue.repositories, job };
    },
    save: saveViaApi,
    resolvePage: async (url) => {
      const page = await api.post<ResolvedPage>("/api/confluence/resolve", { url });
      if (page === undefined) throw new ApiError(502, "internal_error", "no page returned", null);
      return page;
    },
  };
}

/* ---------------------------------------------------------------------------------------------------
 * The house format, shown rather than described
 * ------------------------------------------------------------------------------------------------- */

/** The example bullets. Fixed text: this is the FORMAT on display, not a sample of anyone's repository. */
const PREVIEW_ITEMS = [
  { category: "breaking" as const, headline: "Old sign-in links stop working on 1 March", detail: "Ask your team to sign in again once." },
  { category: "added" as const, headline: "You can now export a report as a spreadsheet", detail: null },
  { category: "fixed" as const, headline: "Attachments over 10 MB no longer fail silently", detail: null },
];

const CADENCE_HEADINGS: Readonly<Record<Cadence, string>> = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
};

/**
 * The cadence as the published heading reads it — the mirror of `cadenceLabel` in
 * `server/changelog/render.ts`, including its refusal to render 24 as "Daily".
 */
export function previewCadenceLabel(cadence: Cadence, intervalHours: number): string {
  if (cadence !== "hourly") return CADENCE_HEADINGS[cadence];
  return intervalHours > 1 ? `Every ${intervalHours} hours` : CADENCE_HEADINGS.hourly;
}

/**
 * Mirrors `server/changelog/render.ts`. If that renderer changes, this example is what goes stale.
 *
 * **The two destinations no longer share a heading, so the preview shows both.** A Confluence section is
 * a DAY and is headed by its date alone (`renderDayHeading`); a Slack message is one RUN in a timeline and
 * keeps the cadence, the name and the window (`renderHeading`), because a message headed only by a date
 * says nothing about which update posted it. Showing one and labelling it "the heading" would be wrong for
 * whichever destination the reader actually chose.
 */
function FormatPreview({
  name,
  cadence,
  intervalHours,
}: {
  readonly name: string;
  readonly cadence: Cadence;
  readonly intervalHours: number;
}): JSX.Element {
  const shown = name.trim() === "" ? "Your update's name" : name.trim();
  // A fixed date, and its real weekday: 1 March 2026 is a Sunday. A preview that named the wrong day of
  // the week would be the first thing a reader noticed and the last thing anybody thought to check.
  const pageHeading = "Sunday March 1 2026";
  const messageHeading = `${previewCadenceLabel(cadence, intervalHours)} update — ${shown} — 1 Mar 2026 – 8 Mar 2026`;

  return (
    <div className="stack">
      <p className="field__hint">On the Confluence page, one section per day:</p>
      <p className="mono">{pageHeading}</p>
      <p className="field__hint">In Slack, one message per run:</p>
      <p className="mono">{messageHeading}</p>
      <ul className="stack">
        {PREVIEW_ITEMS.map((item) => (
          <li key={item.category}>
            <span aria-hidden="true">{CATEGORY_ICONS[item.category]}</span>{" "}
            <span className="visually-hidden">{CATEGORY_LABELS[item.category]}: </span>
            {item.headline}
            {item.detail === null ? null : ` — ${item.detail}`}
          </li>
        ))}
      </ul>
      <p className="field__hint">
        14 commits reviewed across 3 repositories · 6 routine changes omitted.
      </p>
    </div>
  );
}

function IconLegend(): JSX.Element {
  return (
    <ul className="stack">
      {CATEGORIES.map((category) => (
        <li className="row" key={category}>
          <span aria-hidden="true">{CATEGORY_ICONS[category]}</span>
          <span>{CATEGORY_LABELS[category]}</span>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------------------------------------------
 * The screen
 * ------------------------------------------------------------------------------------------------- */

export interface JobEditorProps {
  readonly buId: string;
  /** Null or absent creates; an id edits. */
  readonly jobId?: string | null;
  readonly onSaved?: (jobId: string) => void;
  readonly onCancel?: () => void;
  /** `bu_admin` in this business unit — the role that may change whose authority an update runs with. */
  readonly canReassign?: boolean;
  /** Called after the update is deleted; the caller decides where to go next. */
  readonly onDeleted?: () => void;
  /** Injected by tests. Production passes nothing and gets the API. */
  readonly ports?: JobEditorPorts;
  readonly adminPorts?: JobAdminPorts;
}

export function JobEditor({
  buId,
  jobId = null,
  onSaved,
  onCancel,
  canReassign = false,
  onDeleted,
  ports,
  adminPorts,
}: JobEditorProps): JSX.Element {
  const active = useMemo(() => ports ?? defaultPorts(jobId), [ports, jobId]);

  const [data, setData] = useState<JobEditorData | null>(null);
  /**
   * The roster, for the reassignment picker — best-effort, and only when it could be of use.
   *
   * It belongs to the `users` section, so an author who is not also a user-administrator is simply refused
   * it. That is why the failure is swallowed rather than surfaced: the control below degrades to an account
   * id field, which still works, and an error banner about a list the reader never asked for would be noise.
   */
  const [candidates, setCandidates] = useState<readonly AdminUser[]>([]);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  const [name, setName] = useState("");
  // The changelog leads because it is what every existing update is, and because it is the kind that
  // cannot write anything anywhere. A default that could commit a file into a repository would be a
  // default nobody chose.
  const [kind, setKind] = useState<UpdateKind>("changelog");
  const [cadence, setCadence] = useState<Cadence>("weekly");
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [sendTime, setSendTime] = useState("09:00");
  // The interval cadence's half of the send time, kept as its own string for the same reason
  // `intervalHours` is: a half-typed "3" must be a field error with a sentence, not a silent rewrite.
  // Two states rather than one shared by both fields, so switching cadence to look at the other schedule
  // and switching back does not quietly rewrite the one you had.
  const [sendMinute, setSendMinute] = useState("0");
  const [weekday, setWeekday] = useState("1");
  // Held as the string the user typed rather than as a number, so that a half-typed value is a field
  // error with a sentence rather than a number field that silently rewrites what is being typed.
  const [intervalHours, setIntervalHours] = useState("6");
  const [enabled, setEnabled] = useState(true);
  const [repoPaths, setRepoPaths] = useState<readonly string[]>([]);
  const [confluence, setConfluence] = useState<readonly ConfluenceDestination[]>([]);
  const [slackChannels, setSlackChannels] = useState<readonly string[]>([]);
  const [backfillDays, setBackfillDays] = useState(String(DEFAULT_BACKFILL_DAYS));
  const [ignorePaths, setIgnorePaths] = useState("");
  const [ignoreAuthors, setIgnoreAuthors] = useState("");
  const [postWhenEmpty, setPostWhenEmpty] = useState(false);

  const [spaceKey, setSpaceKey] = useState("");
  const [pageTitle, setPageTitle] = useState("");
  const [channelId, setChannelId] = useState("");

  // Pasting leads, because a destination that already exists is the common case and the safe one — it is
  // bound to the page rather than to a title. Naming a new page is a peer, not a fallback.
  const [pageMode, setPageMode] = useState<PageMode>("paste");
  const [pastedUrl, setPastedUrl] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [found, setFound] = useState<ResolvedPage | null>(null);

  const [errors, setErrors] = useState<DraftErrors>({});
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;
    setLoading(true);
    setLoadError(null);

    active
      .load(controller.signal)
      .then((loaded) => {
        if (!live) return;
        setData(loaded);
        setLoading(false);
        const job = loaded.job;
        if (job === null) return;
        setName(job.job.name);
        // Hydrated like every other field, and then shown as a fact rather than as a control: the kind is
        // immutable, so the editor states it and offers nothing to change it. Leaving it at the "changelog"
        // default here would be the quiet version of the same bug the create-only spread in `saveViaApi`
        // guards — a form that believes an executive summary is a changelog validates it as one, offers it
        // a many-repository picker, and warns it about nothing.
        setKind(job.job.kind);
        setCadence(job.job.cadence);
        setTimezone(job.job.timezone);
        setSendTime(formatMinuteOfDay(job.job.sendAtMinute));
        // The same stored column read the way the cadence in force actually uses it. An interval job
        // saved before 2026-08-06 carries an hour nothing ever read, so showing `sendAtMinute % 60` is
        // not a lossy display — it is the first honest one.
        setSendMinute(String(job.job.sendAtMinute % 60));
        setWeekday(String(job.job.sendOnWeekday ?? 1));
        setIntervalHours(String(job.job.intervalHours));
        setEnabled(job.job.enabled);
        setRepoPaths(job.repos.map((repo) => repo.repoPath));
        setConfluence(
          job.targets
            .filter((target) => target.kind === "confluence")
            .map((target) => ({
              spaceKey: target.spaceKey ?? "",
              pageTitle: target.pageTitle ?? "",
              // Carried through, or a linked destination would silently degrade to by-title on the very
              // next save from this form.
              ...(target.pageId === null ? {} : { pageId: target.pageId }),
            })),
        );
        setSlackChannels(
          job.targets
            .filter((target) => target.kind === "slack")
            .map((target) => target.channelId ?? "")
            .filter((id) => id !== ""),
        );
        setBackfillDays(String(job.job.backfillDays));
        setIgnorePaths((job.job.ignorePaths ?? []).join(", "));
        setIgnoreAuthors((job.job.ignoreAuthors ?? []).join(", "));
        setPostWhenEmpty(job.job.postWhenEmpty);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setLoadError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
        setLoading(false);
      });

    // Best-effort, and only where it could matter: the picker exists to repair an orphaned update.
    if (canReassign && jobId !== null) {
      (adminPorts ?? jobAdminPorts)
        .loadCandidates(controller.signal)
        .then((loaded) => {
          if (live) setCandidates(loaded);
        })
        .catch(() => undefined);
    }

    return () => {
      live = false;
      controller.abort();
    };
  }, [active, buId, attempt]);

  const draft = useMemo<JobDraft>(
    () => ({
      name,
      kind,
      cadence,
      timezone,
      // One column, two schedules, and the cadence decides which field fills it. An interval stores its
      // minute past the hour directly — 0..59 is already a valid minute-past-midnight, so no encoding is
      // invented and a job switched from `daily` to `hourly` and back loses nothing but the hour that
      // never meant anything on the interval side.
      sendAtMinute:
        cadence === INTERVAL_CADENCE
          ? (parseMinutePastHour(sendMinute) ?? 0)
          : (parseTimeOfDay(sendTime) ?? 0),
      sendOnWeekday: WEEKDAY_CADENCES.has(cadence) ? Number(weekday) : null,
      intervalHours: parseIntervalHours(intervalHours) ?? MIN_INTERVAL_HOURS,
      enabled,
      repoPaths,
      confluence,
      slackChannels,
      // Coerced exactly as `intervalHours` above is, and for the same reason. `validateDraft` refuses an
      // out-of-range value only while the field is on screen — it is a changelog input and a document
      // kind has no card holding it — so without this a stale "500", typed before the kind was switched,
      // would ride to a server whose schema caps it at 90 and take a 400 for a field the form was not
      // showing. On a changelog the validator has already stopped the save, so this line changes nothing
      // there.
      backfillDays: sanitisedBackfillDays(backfillDays),
      ignorePaths: parseList(ignorePaths),
      ignoreAuthors: parseList(ignoreAuthors),
      postWhenEmpty,
    }),
    [
      name,
      kind,
      cadence,
      timezone,
      sendTime,
      sendMinute,
      weekday,
      intervalHours,
      enabled,
      repoPaths,
      confluence,
      slackChannels,
      backfillDays,
      ignorePaths,
      ignoreAuthors,
      postWhenEmpty,
    ],
  );

  /**
   * Look the pasted link up. The server does the whole of it — parse, origin check, read, floor — and the
   * answer is the API's space key and title, never anything read out of the URL.
   */
  const lookUp = useCallback((): void => {
    const url = pastedUrl.trim();
    if (url === "") return;
    setResolving(true);
    setResolveError(null);
    setFound(null);
    active
      .resolvePage(url)
      .then((page) => {
        setResolving(false);
        // NOT added: the confirmation below is the only control that catches a short link that decoded to
        // a real but unrelated page, and it is the moment the ownership warning stops being about "a
        // page" and becomes about THIS page.
        setFound(page);
      })
      .catch((cause: unknown) => {
        setResolving(false);
        // The server writes these sentences itself — they name the remedy, which the generic table
        // cannot. Anything without one falls back to the client's own wording.
        if (!(cause instanceof ApiError)) {
          setResolveError("The link could not be looked up. Try again in a moment.");
          return;
        }
        setResolveError(
          cause.status === 401 || cause.message.trim() === "" ? cause.userMessage : cause.message,
        );
      });
  }, [active, pastedUrl]);

  const useFoundPage = useCallback((): void => {
    if (found === null) return;
    const entry: ConfluenceDestination = {
      spaceKey: found.spaceKey,
      pageTitle: found.pageTitle,
      pageId: found.pageId,
    };
    setConfluence((current) =>
      current.some((existing) => destinationKey(existing) === destinationKey(entry))
        ? current
        : [...current, entry],
    );
    setFound(null);
    setPastedUrl("");
    setResolveError(null);
  }, [found]);

  /**
   * Choosing the kind — and the one other piece of state it must reach into.
   *
   * A document kind's picker holds exactly ONE repository, so a selection made while the changelog was
   * chosen has to be cut down to what the control on screen can show. Leaving three paths in state behind
   * a control displaying one is precisely "the form sends something other than what it shows": the save
   * would write the job row, then POST a second repository, and the server would refuse it half-way
   * through — with the update already created and already wrong.
   */
  const chooseKind = useCallback((value: string): void => {
    const chosen = UPDATE_KIND_OPTIONS.find((option) => option === value);
    if (chosen === undefined) return;
    setKind(chosen);
    if (isDocumentKind(chosen)) setRepoPaths((current) => current.slice(0, 1));
  }, []);

  const submit = useCallback((): void => {
    const found = validateDraft(draft, sendTime, backfillDays, intervalHours, sendMinute);
    setErrors(found);
    setSaveError(null);
    // The refusal is the whole point of this branch: nothing is sent, so a job with no repository cannot
    // be created by pressing Save twice quickly or by a validation message being scrolled past.
    if (hasErrors(found)) return;

    setSaving(true);
    active
      .save(draft, data?.job ?? null)
      .then((savedId) => {
        setSaving(false);
        onSaved?.(savedId);
      })
      .catch((cause: unknown) => {
        setSaving(false);
        setSaveError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
        // Re-read: a partial save leaves the server holding something other than what is on screen, and
        // guessing which of the writes landed is exactly the guess this reload removes.
        setAttempt((current) => current + 1);
      });
  }, [active, data, draft, backfillDays, onSaved, sendTime]);

  if (loading) return <LoadingState label="Loading the update" />;

  if (loadError !== null) {
    return (
      <Page title={jobId === null ? "New update" : "Edit update"}>
        <ErrorState
          message={loadError.userMessage}
          traceId={loadError.traceId}
          onRetry={() => setAttempt((current) => current + 1)}
        />
      </Page>
    );
  }

  const repositories = data?.repositories ?? [];

  return (
    <Page
      title={jobId === null ? "New update" : "Edit update"}
      measure="read-wide"
      lede="You choose what is watched and where it goes. Everything about how the update reads is fixed."
    >
      {saveError !== null ? (
        <Banner tone="danger">
          {saveError.userMessage}
          {saveError.traceId === null ? "" : ` (trace ${saveError.traceId})`}
        </Banner>
      ) : null}

      {/*
        FIRST, above the schedule, because it decides what the rest of this form means: which repository
        picker is offered, how many repositories are enough, whether the two changelog cards exist, and
        whether choosing a repository can cause a commit into it.

        A RADIO GROUP rather than a disclosure or a select, for the reason the destination mode selector
        gives a few hundred lines below and for one more of its own. Three peers, each with a hint that is
        the whole of the difference between them — a disclosure would imply two of the three are edge
        cases, and a select hides the hints behind an interaction, which turns "which of these do I want?"
        into a question the user has to open three times to answer.
      */}
      <Card
        title="What this update says"
        support="Chosen once, when the update is created. A changelog is written from the commits; the other two are relayed from a document that lives in the repository itself."
      >
        {jobId === null ? (
          <RadioGroup
            legend="What should this update publish?"
            value={kind}
            onChange={chooseKind}
            options={UPDATE_KIND_OPTIONS.map((option) => ({
              value: option,
              label: updateKindLabel(option),
              hint: updateKindHint(option),
            }))}
            hint="This cannot be changed afterwards. The page an update maintains is rebuilt from that update's own history every time it publishes, so a kind that changed would leave a page whose past and future are different things."
          />
        ) : (
          /*
            THE KIND IS IMMUTABLE, so an existing update is SHOWN it rather than offered it.
            A disabled radio group would be the same information with a control the reader may reasonably
            try to operate; the server refuses a PATCH that changes the kind, and `saveViaApi` does not
            even send the field on a save of an existing update. There is nothing here to disable.
          */
          <div className="stack">
            <p className="field__label">What this update publishes</p>
            <p className="row">
              <Badge tone="info">{updateKindLabel(kind)}</Badge>
            </p>
            <p className="field__hint">{updateKindHint(kind)}</p>
            <p className="field__hint">
              An update&rsquo;s kind is fixed when it is created and cannot be changed here or anywhere
              else. To publish something else, make a second update — this one keeps its page and its
              history.
            </p>
          </div>
        )}
      </Card>

      <Card title="Schedule" support="When the update is assembled and sent. The next due time is computed by the server, never chosen here.">
        <TextField
          label="Name"
          value={name}
          onChange={setName}
          required
          hint="Shown as the heading of every published update."
          {...(errors.name === undefined ? {} : { error: errors.name })}
        />

        <Select
          label="How often"
          value={cadence}
          options={CADENCE_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
          onChange={(value) => {
            const chosen = CADENCE_OPTIONS.find((option) => option.value === value);
            if (chosen !== undefined) setCadence(chosen.value);
          }}
        />

        {/*
          `inputMode="numeric"` rather than `type="number"`: it raises the numeric keypad on a phone,
          which is the point, without the spinner that changes a value on a stray scroll and without
          `type=number`'s habit of reporting a partly-typed entry as empty. The bound is stated in the
          hint and enforced by `parseIntervalHours`, where a refusal can be a sentence.
        */}
        {cadence === INTERVAL_CADENCE ? (
          <TextField
            label="Hours between updates"
            inputMode="numeric"
            value={intervalHours}
            onChange={setIntervalHours}
            required
            hint={describeInterval(intervalHours)}
            {...(errors.intervalHours === undefined ? {} : { error: errors.intervalHours })}
          />
        ) : null}

        {WEEKDAY_CADENCES.has(cadence) ? (
          <Select
            label="Day of the week"
            value={weekday}
            options={WEEKDAY_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
            onChange={setWeekday}
          />
        ) : null}

        {/*
          THE INTERVAL CADENCE HAS NO TIME OF DAY, so it is not offered one.

          The server takes `send_at_minute % 60` for `hourly` (`engine/cadence.ts`, `nextHourly`) and the
          hour is discarded — an interval has a phase, not an o'clock. Until 2026-08-06 this field was
          "Send at 09:00 — 24-hour local time" on every cadence, so a six-hourly job configured for nine
          o'clock ran at 03:00, 09:00, 15:00 and 21:00 and the hour the user typed did nothing whatever.
          `lib/format.ts` had said so in its own comment since it was written, and printed ":00 past" on
          the dashboard; this field was the last surface still asking a question it would not honour.

          Two fields rather than one relabelled, because the INPUT has to change too: a time picker
          cannot express "past the hour", and a user who can type 09:30 into it will reasonably expect
          09:30 to mean something.
        */}
        {cadence === INTERVAL_CADENCE ? (
          <TextField
            label="Minutes past the hour"
            inputMode="numeric"
            value={sendMinute}
            onChange={setSendMinute}
            required
            hint={describeSendMinute(sendMinute)}
            {...(errors.sendMinute === undefined ? {} : { error: errors.sendMinute })}
          />
        ) : (
          <TextField
            label="Send at"
            value={sendTime}
            onChange={setSendTime}
            required
            hint="24-hour local time in the zone below."
            {...(errors.sendTime === undefined ? {} : { error: errors.sendTime })}
          />
        )}

        <TextField
          label="Time zone"
          value={timezone}
          onChange={setTimezone}
          required
          hint="An IANA name, such as Europe/London. The send time is read in this zone, not the reader's."
          {...(errors.timezone === undefined ? {} : { error: errors.timezone })}
        />

        <Checkbox
          label="Enabled"
          checked={enabled}
          onChange={setEnabled}
          hint="A paused update keeps its settings and its history, and is not scheduled."
        />
      </Card>

      {isDocumentKind(kind) ? (
        <Card
          title="Repository"
          support="Offered from this business unit's manifest. The list is filtered on the server by what you may reach; nothing outside it can be chosen, whatever is typed."
        >
          {/*
            ⚠️ BESIDE THE CONTROL THAT CAUSES IT, at the moment of choosing — the same rule as the
            page-ownership warning in Destinations, and for the same reason: a warning in help text is a
            warning nobody read until afterwards. This one is about a commit made to somebody else's
            repository, unattended, on a schedule, so it is not the lighter of the two.

            The second half is not softening. It is the property that makes the first half acceptable and
            it is structural rather than promised: `GitWriter` has `readFile` and `createFile` and no
            update method at all, so there is no call with which Ticketless could overwrite a document —
            and `ticket_document_marks` records the one push it may ever make to a given file.
          */}
          <Banner tone="warning">
            If <span className="mono">{SOURCE_FILE[kind]}</span> is not in the repository you choose, this
            update <strong>writes one and commits it to that repository</strong> — a real commit, on the
            branch the manifest names, made unattended when the update next runs. It will never modify or
            overwrite a <span className="mono">{SOURCE_FILE[kind]}</span> that is already there: once the
            file exists it belongs to whoever edits it next, and every later update simply relays what it
            says.
          </Banner>

          {/*
            ONE repository, and the control is what enforces it rather than a message afterwards. An
            executive summary answers "what is this thing and what is it worth" — a question about one
            product. Two products under one heading is not a fuller answer; it is two answers pretending
            to be one, under a heading that can only be true of one of them.
          */}
          <Select
            label="The repository this update is about"
            value={repoPaths[0] ?? ""}
            options={[
              {
                value: "",
                label:
                  repositories.length === 0
                    ? "No repositories are available to you"
                    : "— choose a repository —",
              },
              ...repositories.map((repo) => ({ value: repo.path, label: repo.workspacePath })),
            ]}
            onChange={(value) => setRepoPaths(value === "" ? [] : [value])}
            required
            hint={
              repositories.length === 0
                ? "No repositories are available to you in this business unit. That is a grant, not a bug — an administrator adds them through a group."
                : `Exactly one: ${updateKindLabel(kind)} is about a single product, and its page is headed as though it were.`
            }
            {...(errors.repoPaths === undefined ? {} : { error: errors.repoPaths })}
          />
        </Card>
      ) : (
      <Card
        title="Repositories"
        support="Offered from this business unit's manifest. The list is filtered on the server by what you may reach; nothing outside it can be added, whatever is typed."
      >
        <MultiSelect
          label="Repositories to watch"
          options={repositories.map((repo) => ({ value: repo.path, label: repo.workspacePath }))}
          selected={repoPaths}
          onChange={setRepoPaths}
          emptyLabel="No repositories chosen"
          hint={
            repositories.length === 0
              ? "No repositories are available to you in this business unit. That is a grant, not a bug — an administrator adds them through a group."
              : "An update with no repositories polls nothing and reports nothing."
          }
        />
        {errors.repoPaths === undefined ? null : (
          <p className="field__error" role="alert">
            <span aria-hidden="true">!</span> {errors.repoPaths}
          </p>
        )}
        {repoPaths.length === 0 ? null : (
          <ul className="stack">
            {repoPaths.map((path) => (
              <li className="row" key={path}>
                <span className="mono">{path}</span>
                <Button
                  variant="link"
                  size="sm"
                  label={`Remove ${path}`}
                  onClick={() => setRepoPaths(repoPaths.filter((candidate) => candidate !== path))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
      )}

      <Card title="Destinations" support="Where each update is published. A destination this business unit is not allowed to write to is refused when you save, not here.">
        {errors.destinations === undefined ? null : (
          <p className="field__error" role="alert">
            <span aria-hidden="true">!</span> {errors.destinations}
          </p>
        )}

        <Banner tone="warning">
          Choosing a Confluence page hands that page to Ticketless. Its whole contents are rewritten from
          Ticketless&rsquo;s own records every time this update publishes, so anything a person writes on it
          will be replaced. Point this at a page nobody edits by hand.
        </Banner>

        {/*
          A radio group rather than a disclosure, deliberately. A disclosure would imply the second path
          is an edge case, and creating a page is how most first destinations start; both must read as
          equally legitimate. Pasting is the default because it binds the destination to the page itself.
        */}
        <RadioGroup
          legend="How do you want to choose the page?"
          value={pageMode}
          onChange={(value) => setPageMode(value === "name" ? "name" : "paste")}
          options={[
            {
              value: "paste",
              label: "Paste a link to a page",
              hint: "For a page that already exists. The destination is bound to the page, so renaming it in Confluence is safe.",
            },
            {
              value: "name",
              label: "Name a new page",
              hint: "For a page that does not exist yet. Ticketless creates it the first time this update publishes.",
            },
          ]}
        />

        {pageMode === "paste" ? (
          <div className="stack">
            <TextField
              label="Confluence page link"
              value={pastedUrl}
              onChange={setPastedUrl}
              type="url"
              inputMode="url"
              hint="Open the page in Confluence and copy the link from your browser's address bar."
              placeholder="https://…/wiki/spaces/ENG/pages/1234567/Weekly+changes"
              {...(resolveError === null ? {} : { error: resolveError })}
            />
            <Button
              variant="secondary"
              loading={resolving}
              disabled={pastedUrl.trim() === "" || resolving}
              onClick={lookUp}
            >
              Look up page
            </Button>

            {found === null ? null : (
              <div className="stack" role="status" aria-live="polite">
                <h3>Found this page</h3>
                <p>
                  <strong>{found.pageTitle}</strong>
                </p>
                <p className="mono">
                  {found.spaceKey} · page {found.pageId}
                </p>
                <p>
                  <a href={found.url} target="_blank" rel="noopener noreferrer">
                    Open in Confluence ↗
                  </a>
                </p>
                {found.idConfidence === "decoded" ? (
                  <Banner tone="warning">
                    This was a short link. Short links are decoded rather than read, so check this is the
                    page you meant.
                  </Banner>
                ) : null}
                <div className="row">
                  <Button variant="primary" onClick={useFoundPage}>
                    Use this page
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setFound(null);
                      setPastedUrl("");
                    }}
                  >
                    Not this one
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="row">
            <TextField
              label="Confluence space key"
              value={spaceKey}
              onChange={setSpaceKey}
              hint="Must be in this business unit's write allowlist. Case matters."
            />
            <TextField
              label="Page title"
              value={pageTitle}
              onChange={setPageTitle}
              hint="Exactly as it should appear. If a page with this title already exists in the space, Ticketless takes it over; otherwise it creates it."
            />
            <Button
              variant="secondary"
              disabled={spaceKey.trim() === "" || pageTitle.trim() === ""}
              onClick={() => {
                const entry = { spaceKey: spaceKey.trim(), pageTitle: pageTitle.trim() };
                if (confluence.some((existing) => destinationKey(existing) === destinationKey(entry))) return;
                setConfluence([...confluence, entry]);
                setSpaceKey("");
                setPageTitle("");
              }}
            >
              Add page
            </Button>
          </div>
        )}

        {confluence.length === 0 ? null : (
          <ul className="stack">
            {confluence.map((entry) => (
              <li className="row" key={destinationKey(entry)}>
                <Badge tone="info">Confluence</Badge>
                <span className="mono">
                  {entry.spaceKey} / {entry.pageTitle}
                </span>
                {entry.pageId === undefined ? (
                  <Badge tone="neutral">by title</Badge>
                ) : (
                  <Badge tone="success">linked</Badge>
                )}
                <Button
                  variant="link"
                  size="sm"
                  label={`Remove ${entry.spaceKey} ${entry.pageTitle}`}
                  onClick={() =>
                    setConfluence(
                      confluence.filter((candidate) => destinationKey(candidate) !== destinationKey(entry)),
                    )
                  }
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* True today, and previously written down nowhere a user could see it. The cure is the paste box
            immediately above, which is why the sentence names it. */}
        {confluence.some((entry) => entry.pageId === undefined) ? (
          <p className="field__hint">
            A destination chosen <strong>by title</strong> is matched by its exact title each time it
            publishes. If the page is renamed in Confluence, the next publish creates a new page under the
            old title. Paste a link instead to bind the destination to the page itself.
          </p>
        ) : null}

        <div className="row">
          <TextField
            label="Slack channel ID"
            value={channelId}
            onChange={setChannelId}
            hint="The channel ID, never the name — a name can be re-pointed at a different channel."
          />
          <Button
            variant="secondary"
            disabled={channelId.trim() === ""}
            onClick={() => {
              const entry = channelId.trim();
              if (slackChannels.includes(entry)) return;
              setSlackChannels([...slackChannels, entry]);
              setChannelId("");
            }}
          >
            Add channel
          </Button>
        </div>

        {slackChannels.length === 0 ? null : (
          <ul className="stack">
            {slackChannels.map((id) => (
              <li className="row" key={id}>
                <Badge tone="info">Slack</Badge>
                <span className="mono">{id}</span>
                <Button
                  variant="link"
                  size="sm"
                  label={`Remove ${id}`}
                  onClick={() => setSlackChannels(slackChannels.filter((candidate) => candidate !== id))}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/*
        BOTH CARDS BELOW ARE THE CHANGELOG'S, and neither is merely irrelevant to the other two kinds.

        "What counts as worth reporting" is entirely about commits: ignore lists, bots, a filter, a
        reconstruction of past days. A document kind reads one file as it stands today — there is no
        window, no watermark and no history to reconstruct, so every control here would be a question
        whose answer is discarded, which is the defect the two schedule fields were split to close.

        "How every update reads" is worse than irrelevant: it shows the eight-icon bullet vocabulary and a
        worked example of a day section, and it introduces them as *the* house format. That claim is false
        of an executive summary and a security overview, which are pages of prose in named sections with
        no bullets and no icons at all. Left on screen it would be the form telling a user, in a card whose
        whole purpose is to say "this is the output", something that is not the output.

        `validateDraft` follows the same rule from the other side: the backfill field is checked only when
        this card is the one on screen.
      */}
      {kind === "changelog" ? (
      <>
      <Card
        title="What counts as worth reporting"
        support="Bots, lockfiles and anything you ignore below are removed before a word is written — those are counted and reported in the footer. Everything else is read, and what is worth telling a reader about is decided then, not by a number you have to pick. The footer says how many were read and passed over, so a quiet week never looks like a broken update."
      >
        <TextField
          label="Reconstruct history when first published"
          value={backfillDays}
          onChange={setBackfillDays}
          inputMode="numeric"
          hint={`Days of past work to write up the first time this update sees a repository, so the page is worth reading from day one. 0 to ${MAX_BACKFILL_DAYS}; zero starts from today. Adding a repository later reconstructs that one too.`}
          {...(errors.backfillDays === undefined ? {} : { error: errors.backfillDays })}
        />
        <TextField
          label="Ignore paths"
          value={ignorePaths}
          onChange={setIgnorePaths}
          hint="Comma-separated globs, e.g. docs/**, **/*.md. Generated artefacts are already ignored."
        />
        <TextField
          label="Ignore authors"
          value={ignoreAuthors}
          onChange={setIgnoreAuthors}
          hint="Comma-separated. Bots are already ignored by name pattern."
        />
        <Checkbox
          label="Publish even when there is nothing to report"
          checked={postWhenEmpty}
          onChange={setPostWhenEmpty}
          hint="On: readers see that the update ran and found nothing. Off: silence, which is indistinguishable from a broken job."
        />
      </Card>

      <Card
        title="How every update reads"
        support="This is not a setting. Wording, ordering, icons and the footer are the same for every update in every business unit, by design — so a reader learns the format once."
      >
        <div className="section">
          <h3>Icons</h3>
          <IconLegend />
        </div>
        <div className="section">
          <h3>
            Example — {previewCadenceLabel(cadence, draft.intervalHours).toLowerCase()} update in the
            house format
          </h3>
          <FormatPreview name={name} cadence={cadence} intervalHours={draft.intervalHours} />
        </div>
      </Card>
      </>
      ) : null}

      {/*
        THE END OF THE FORM, and the actions that conclude it.

        They used to sit in the page head, which put Save at the one point in the page where the user had
        decided nothing yet — and, on a phone, scrolled it off screen the moment they began typing, so the
        way back to it was past every field they had just filled. `02-patterns.md` §2.1 makes the exception
        for exactly this shape ("a long form"), and `03-components.md` §2 asks for a full-width primary on
        mobile; `FormActions` carries both.

        ⚠️ ABOVE `JobAdmin`, not below it. Those controls act when PRESSED — reassign, delete, update now —
        and Save commits the fields above. Putting Save after them would make somebody scroll past a delete
        button to save their work, and would seat the primary action beside a destructive one, which
        `03-components.md` §2 forbids.
      */}
      <FormActions>
        {onCancel === undefined ? null : (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button variant="primary" loading={saving} onClick={submit}>
          Save update
        </Button>
      </FormActions>

      {/* Only on an update that exists. These act when pressed rather than on Save, because each is a
          decision the server re-checks at the moment it is made — see JobAdmin's header. */}
      {data?.job == null ? null : (
        <JobAdmin
          jobId={data.job.job.id}
          jobName={data.job.job.name}
          owner={data.job.job.owner}
          targets={data.job.targets}
          canReassign={canReassign}
          candidates={candidates}
          onChanged={() => setAttempt((current) => current + 1)}
          {...(onDeleted === undefined ? {} : { onDeleted })}
          ports={adminPorts ?? jobAdminPorts}
        />
      )}
    </Page>
  );
}
